//import { MyHashTable } from "../Common/Helpers";
import { html_entity_decode } from "../Common/Common_d";
import Appointment from "./Event";
import Place, { Place_Source } from "./Place";
import Places from "./Places";
import Common, { Settings, SortByEnum, COLORS, MyHashTable } from "../Common/Common";
import { DateTime, Interval, Duration } from "luxon";
import { eventMatchesSearch } from "../Common/scheduleSearch";

// A short wait is worth it to catch a place that's about to open (the
// "arrived 15-30 minutes too soon" pattern) rather than either accepting
// the violation or skipping to a worse-fitting candidate; a long wait isn't
// worth stalling the whole day for, so it falls through to the plain
// fallback tier instead.
const AVOID_CLOSED_MAX_WAIT_MINUTES = 45;

// Greedy nearest-feasible-neighbor ordering for "Optimize by Location" —
// at each step, tries three tiers in order: (1) the nearest remaining place
// that's already open at its estimated arrival time; (2) failing that, the
// place needing the shortest wait (capped at AVOID_CLOSED_MAX_WAIT_MINUTES)
// among those closed right now but opening again later today — advancing
// the clock to catch it rather than eating an avoidable violation; (3) if
// NEITHER tier has a candidate (e.g. everything remaining is closed all day
// or would need an unreasonably long wait), the nearest place regardless of
// hours, so a day with incompatible/unknown hours still gets a complete,
// non-stuck order instead of being left empty. Estimates arrival times
// using the exact same travel-time formula as the timing loop this order is
// later fed into (recalculateDay/recalculateSchedule below) — an
// approximation good enough to decide order; those loops still compute the
// authoritative final Start/End afterward.
function orderPlacesAvoidingClosed(withPlaces, dayDate, rounding_to, use_place_duration, event_default_duration) {
  const remaining = withPlaces.slice();
  const ordered = [];
  let currentTime = dayDate;
  let lastPlace = null;
  let first = true;

  // Was its own distance*10 (or distance*2 once that exceeds 45 minutes)
  // formula, independent of Common.estimateTravelTimeMinutes — the function
  // the real timing loop (recalculateDay/recalculateSchedule) uses to set
  // actual Start/End times. The two formulas could disagree by a lot (a
  // ~5km hop: ~15min here vs. ~40min there), so this function would pick an
  // order it believed avoided closed-hours conflicts based on optimistic
  // arrival estimates, only for the real (longer) travel times to arrive
  // later than predicted and hit conflicts anyway — "optimize by location"
  // creating more opening-hours conflicts than before, not fewer. Now uses
  // the exact same estimate the final schedule will be judged against.
  const travelTime = (fromPlace, toPlace) => Common.estimateTravelTimeMinutes(fromPlace, toPlace, rounding_to);

  while (remaining.length > 0) {
    let best = null;
    let bestOpen = null;
    let bestWait = null;
    for (let i = 0; i < remaining.length; i++) {
      const candidate = remaining[i];
      const travel = first ? 0 : travelTime(lastPlace, candidate.place);
      const arrival = first ? currentTime : currentTime.plus({ minutes: travel });
      const distanceKey = lastPlace
        ? Places.calculateDistance(
            { latitude: lastPlace.Lat, longitude: lastPlace.Longi },
            { latitude: candidate.place.Lat, longitude: candidate.place.Longi }
          )
        : 0;
      const info = { idx: i, travel, distanceKey };
      if (!best || distanceKey < best.distanceKey) best = info;

      const isOpen = Place.isOpenAt(candidate.place, arrival);
      if (isOpen !== false) {
        if (!bestOpen || distanceKey < bestOpen.distanceKey) bestOpen = info;
        continue;
      }
      const { closedAllDay, minutesUntilOpen } = Place.openingStatusAt(candidate.place, arrival);
      if (closedAllDay || minutesUntilOpen == null || minutesUntilOpen > AVOID_CLOSED_MAX_WAIT_MINUTES) continue;
      const waitInfo = { ...info, waitMinutes: minutesUntilOpen };
      if (
        !bestWait ||
        waitInfo.waitMinutes < bestWait.waitMinutes ||
        (waitInfo.waitMinutes === bestWait.waitMinutes && distanceKey < bestWait.distanceKey)
      ) {
        bestWait = waitInfo;
      }
    }
    const chosen = bestOpen || bestWait || best;
    const [picked] = remaining.splice(chosen.idx, 1);
    ordered.push(picked);

    // Same fix as recalculateSchedule's own dayDate advancement/overflow
    // check just below in this file — a first-of-day pick's travel_time is
    // never actually charged, no matter how large (matches
    // ScheduleBuilder.js's addEventOpeningHoursAware too). The
    // "|| chosen.travel > 30" override reintroduced exactly the bug that
    // was found and removed elsewhere in this file: a cross-city travel_time
    // held over in `first`'s estimate distorted every later opening-hours
    // check that day. Found 2026-09-29 alongside the related recalculateSchedule fix.
    if (!first) currentTime = currentTime.plus({ minutes: chosen.travel });
    if (chosen.waitMinutes) currentTime = currentTime.plus({ minutes: chosen.waitMinutes });
    const duration = use_place_duration ? (picked.place.Duration || event_default_duration) : event_default_duration;
    currentTime = currentTime.plus({ minutes: duration });
    lastPlace = picked.place;
    first = false;
  }
  return ordered;
}

export default class Schedule {
  constructor() {
    this.events = new Array();
  }
  decodeSchedule(guideString, scheduleStart, scheduleTag, GuideJson) {
    let _scheduleOBJ = null;
    if (GuideJson != null) {
      for (let i = 0; i < GuideJson.schedule.events.length; i++) {
        var ievent = new Appointment(this);
        ievent.json_to_appointment(this, GuideJson.schedule.events[i], false);
        ievent.ID = this.create_id();
        if (ievent.Places === null) ievent.Places = "";
        this.events.push(ievent);
      }
      this.events.sort(compare);
      // let __shed = this.events.filter((x) => x.Itinerary == "One Day Tour");
      // console.log("__shed", __shed);
    } else {
      if (scheduleStart == -1) _scheduleOBJ = null;
      else {
        //console.log("Schedule");
        var ScheduleString = html_entity_decode(guideString.substring(scheduleStart + scheduleTag.length)); //.replace("\\\\\"", "\\\"").replace("\\r", "\r\n").replace("\\t", "\t").replace("\\n", "\n").replace("\\/", "/").replace("\\ ", "\"").replace("\\\n", "\n");
        _scheduleOBJ = JSON.parse(ScheduleString);
        this.events = new Array();
        for (var key in _scheduleOBJ) {
          var ievent = new Appointment(this);
          ievent.json_to_appointment(this, _scheduleOBJ[key], true);
          ievent.ID = this.create_id();
          this.events.push(ievent);
        }
        this.events.sort(compare);
        // let __shed = this.events.filter((x) => x.Itinerary == "One Day Tour");
        // console.log("__shed", __shed);
        //if (guide_obj.cache_data == true) this.loadDataFromStorage();
      }
    }
  }

  create_id() {
    var r = Math.floor(Math.random() * 1000000000 + 1);
    while (this.get_event(r) != null) r = Math.floor(Math.random() * 1000000000 + 1);
    return r.toString();
  }

  geScheduleNames() {
    let Itineraries = [{ label: "Main", value: "Main" }];
    for (let i = 0; i < this.events.length; i++) {
      if (typeof this.events[i].Itinerary == "undefined") continue;
      const Itinerary = this.events[i].Itinerary.replace(/%20/g, " ");
      const index = Itineraries.findIndex((x) => x.label == Itinerary);
      if (index != -1) continue;
      Itineraries.push({
        label: Itinerary,
        value: Itinerary,
      });
    }
    //Itineraries = Array.from(new Set(Itineraries));
    Itineraries.sort((a, b) => (a.label.toUpperCase() > b.label.toUpperCase() ? 1 : -1));
    //console.log(Itineraries);
    return Itineraries;
  }

  getStartEndDate(itinerary) {
    //console.log("getStartEndDate", itinerary);

    let filtered_events = [];
    for (let i = 0; i < this.events.length; i++) {
      const _itinerary = typeof this.events[i].Itinerary == "undefined" ? "Main" : this.events[i].Itinerary;
      if (_itinerary == itinerary) filtered_events.push(this.events[i]);
    }

    if (filtered_events.length == 0) return { startDate: DateTime.now(), endDate: DateTime.now() };
    let startDate = DateTime.fromISO("3000-11-24", { zone: "utc" });
    let endDate = DateTime.fromISO("1000-11-24", { zone: "utc" });
    for (let i = 0; i < filtered_events.length; i++) {
      if (DateTime.fromISO(filtered_events[i].Start, { zone: "utc" }) < startDate)
        startDate = DateTime.fromISO(filtered_events[i].Start, { zone: "utc" });
      if (DateTime.fromISO(filtered_events[i].End, { zone: "utc" }) > endDate)
        endDate = DateTime.fromISO(filtered_events[i].End, { zone: "utc" });
    }
    //console.log({ startDate: startDate, endDate: endDate });
    return { startDate: startDate, endDate: endDate };
  }

  getFirsEventStartDate(itinerary) {
    const startEndDate = this.getStartEndDate(itinerary);
    return startEndDate.startDate;
  }
  getFirsEventEndDate(itinerary) {
    const startEndDate = this.getStartEndDate(itinerary);
    return startEndDate.endDate;
  }
  // getISODateString(end) {
  //   startEndDate = this.getStartEndDate();
  //   let d = startEndDate.startDate;
  //   if (end == true) d = startEndDate.endDate;
  //   const d_ISO = d.toISOString();
  //console.log(d_ISO);
  //   if (d_ISO.includes("T")) {
  //     const parts = d_ISO.split("T");
  //     return parts[0];
  //   }
  //   return "";
  // }
  getPlaces_InSchedule(itinerary) {
    if (itinerary == null) itinerary = "Main";
    window.listPlaceTimes = new MyHashTable();
    window.listPlaceEvents = new MyHashTable();
    for (var i = 0; i < this.events.length; i++) {
      const _itinerary = typeof this.events[i].Itinerary == "undefined" ? "Main" : this.events[i].Itinerary;
      if (_itinerary != itinerary) continue;
      var placeIDString = this.events[i].Places;
      if (placeIDString == null) continue;
      if (placeIDString == "") continue;
      var placeIDs = placeIDString.split(",");
      for (var j = 0; j < placeIDs.length; j++) {
        if (placeIDs[j] == null) continue;
        if (!window.listPlaceTimes.hasItem(placeIDs[j])) {
          window.listPlaceTimes.setItem(placeIDs[j], new Array());
          window.listPlaceEvents.setItem(placeIDs[j], new Array());
        }
        var dates = window.listPlaceTimes.getItem(placeIDs[j]);
        var date = new Date(this.events[i].Start);
        dates.push(date);
        window.listPlaceTimes.setItem(placeIDs[j], dates);
        var events = window.listPlaceEvents.getItem(placeIDs[j]);
        events.push(this.events[i]);
        window.listPlaceEvents.setItem(placeIDs[j], events);
      }
    }
  }
  countTimesInSchedule() {
    let eventsCountArray = [];
    for (let i = 0; i < this.events.length; i++) {
      if (!this.events[i].Places) continue;
      let placesArray = this.events[i].Places.split(",");
      placesArray.forEach((placeID) => {
        const index = eventsCountArray.findIndex((x) => x.placeID === placeID.trim());
        if (index === -1) {
          const new_entry = { placeID: placeID, count: 1 };
          eventsCountArray.push(new_entry);
        } else eventsCountArray[index].count += 1;
      });
    }
    return eventsCountArray;
  }

  findPlace_InSchedule(searchText, itinerary, place, _Places) {
    if (itinerary == null) itinerary = "Main";
    const events = this.getScheduleToDisplay2(searchText, itinerary, _Places);
    for (let i = 0; i < events.length; i++) {
      for (let j = 0; j < events[i].data.length; j++) {
        let placesString = events[i].data[j].Places;
        let placesArray = placesString.split(",");
        if (placesArray.includes(place.ID)) return { section: i, item: j };
      }
    }
    return null;
  }

  getEventsFromSchedule(itinerary) {
    let events = [];
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary === itinerary) events.push(event);
    });
    return events;
  }

  findEventByName(event_title, itinerary) {
    for (let i = 0; i < this.events.length; i++) {
      const event = this.events[i];
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary === itinerary && event.Subject === event_title) return event;
    }
    return null;
  }
  getScheduleToDisplay(itinerary) {
    if (itinerary == null) itinerary = "Main";
    let eventsList = [];
    //console.log("this.all_places", this.all_places);
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary != itinerary) return;
      eventsList.push({
        id: event.ID.toString(),
        start: event.Start,
        end: event.End,
        title: event.Subject,
        summary: event.Body,
        event: event,
      });
    });
    //placesList.forEach((x) => //console.log(x.Name));
    eventsList.sort((a, b) =>
      new Date(a.start).getTime() < new Date(b.start).getTime()
        ? -1
        : new Date(a.start).getTime() > new Date(b.start).getTime()
        ? 1
        : 0
    );
    return eventsList;
  }

  getEventsToDisplay(itinerary) {
    if (itinerary == null) itinerary = "Main";
    let eventsList = [];
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary != itinerary) return;
      eventsList.push({
        id: event.ID.toString(),
        name: `${Common.formatDate(event.Start)}, ${Common.formatTimeLuxon(event.Start, null)}`,
        value: event,
        label: `${Common.formatDate(event.Start)}, ${Common.formatTimeLuxon(event.Start, null)}`,
        start: event.Start,
        end: event.End,
        title: event.Subject,
        summary: event.Body,
      });
    });
    eventsList.sort((a, b) =>
      new Date(a.start).getTime() < new Date(b.start).getTime()
        ? -1
        : new Date(a.start).getTime() > new Date(b.start).getTime()
        ? 1
        : 0
    );
    return eventsList;
  }

  // getChaptersSectionsToDisplay() {
  //   let sections = [];
  //   // //console.log(this.chapters);
  //   for (let j = 0; j < this.chapters.length; j++) {
  //     let section = { Title: this.chapters[j].Title, data: [], index: j + 1 };
  //     for (let i = 0; i < this.chapters[j].sections.length; i++) {
  //       let _sec = this.chapters[j].sections[i];
  //       _sec.Chapter = this.chapters[j].Title;
  //       _sec.ChapterID = this.chapters[j].ID;
  //       section.data.push(_sec);
  //     }
  //     sections.push(section);
  //   }
  //   // //console.log(sections);
  //   return sections;
  // }

  changeStartDate(oldStartDate, newStartDate, itinerary) {
    //console.log("oldStartDate, newStartDate", oldStartDate, newStartDate);
    if (itinerary == null) itinerary = "Main";
    //var timeDiff = newStartDate.getTime() - oldStartDate.getTime();
    // Diff in whole days only — preserves each event's time-of-day
    var timeDiff = newStartDate.startOf("day").diff(oldStartDate.startOf("day"));
    //console.log("changeStartDate", oldStartDate, newStartDate, timeDiff);

    // days difference
    //var diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary != itinerary) return;
      event.Start = DateTime.fromISO(event.Start, { zone: "utc" })
        .plus({ seconds: timeDiff / 1000 })
        .toISO({ zone: "utc" });
      event.End = DateTime.fromISO(event.End, { zone: "utc" })
        .plus({ seconds: timeDiff / 1000 })
        .toISO({ zone: "utc" });
    });
    //console.log(this.events);
  }

  moveDay(oldStartDate, newStartDate, itinerary) {
    if (itinerary == null) itinerary = "Main";
    var timeDiff = newStartDate.diff(oldStartDate);
    //console.log(oldStartDate, newStartDate, timeDiff);
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary != itinerary) return;
      let startDate = DateTime.fromISO(event.Start, { zone: "utc" });
      if (
        startDate.year === oldStartDate.year &&
        startDate.month === oldStartDate.month &&
        startDate.day === oldStartDate.day &&
        startDate.hour >= oldStartDate.hour &&
        startDate.minute >= oldStartDate.minute
      ) {
        event.Start = DateTime.fromISO(event.Start, { zone: "utc" })
          .plus({ seconds: timeDiff / 1000 })
          .toISO({ zone: "utc" });
        event.End = DateTime.fromISO(event.End, { zone: "utc" })
          .plus({ seconds: timeDiff / 1000 })
          .toISO({ zone: "utc" });
      }
    });
    //console.log(this.events);
  }

  setDayStart(newStartDate, itinerary) {
    ////console.log("newStartDate", newStartDate);
    if (itinerary == null) itinerary = "Main";
    let sortedEvents = [];
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary !== itinerary) return;
      var event_lux = DateTime.fromISO(event.Start);
      //console.log("newStartDate.day", newStartDate.day);
      //console.log("event_lux.day", DateTime.fromISO(event.Start).day);
      if (newStartDate.day !== event_lux.day) return;
      if (newStartDate.month !== event_lux.month) return;
      if (newStartDate.year !== event_lux.year) return;
      sortedEvents.push(event);
    });
    console.log("sortedEvents", sortedEvents);

    sortedEvents = sortedEvents.sort((a, b) =>
      new Date(a.Start).getTime() < new Date(b.Start).getTime()
        ? -1
        : new Date(a.Start).getTime() > new Date(b.Start).getTime()
        ? 1
        : 0
    );
    if (sortedEvents.length === 0) return;
    var timeDiff = newStartDate.diff(DateTime.fromISO(sortedEvents[0].Start));

    sortedEvents.forEach((event) => {
      event.Start = DateTime.fromISO(event.Start, { zone: "utc" })
        .plus({ seconds: timeDiff / 1000 })
        .toISO({ zone: "utc" });
      event.End = DateTime.fromISO(event.End, { zone: "utc" })
        .plus({ seconds: timeDiff / 1000 })
        .toISO({ zone: "utc" });
    });
  }

  shiftDayEvents(oldStartDate, newStartDate, itinerary, exclude, down) {
    if (itinerary == null) itinerary = "Main";
    var timeDiff = newStartDate.diff(oldStartDate);
    //console.log(oldStartDate, newStartDate, timeDiff);
    let count = 0;
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary !== itinerary) return;
      let startDate = DateTime.fromISO(event.Start, { zone: "utc" });
      if (
        startDate.year === oldStartDate.year &&
        startDate.month === oldStartDate.month &&
        startDate.day === oldStartDate.day
      ) {
        if (down) {
          if (startDate >= oldStartDate && event.ID !== exclude.ID) {
            event.Start = DateTime.fromISO(event.Start, { zone: "utc" })
              .plus({ seconds: timeDiff / 1000 })
              .toISO({ zone: "utc" });
            event.End = DateTime.fromISO(event.End, { zone: "utc" })
              .plus({ seconds: timeDiff / 1000 })
              .toISO({ zone: "utc" });
          }
        } else {
          if (startDate <= oldStartDate && event.ID !== exclude.ID) {
            count++;
            if (count >= 2) return;
            event.Start = DateTime.fromISO(event.Start, { zone: "utc" })
              .minus({ seconds: timeDiff / 1000 })
              .toISO({ zone: "utc" });
            event.End = DateTime.fromISO(event.End, { zone: "utc" })
              .minus({ seconds: timeDiff / 1000 })
              .toISO({ zone: "utc" });
            //only do 2
            //return;
          }
        }
      }
    });
    //console.log(this.events);
  }

  shiftAllEvents(oldStartDate, newStartDate, itinerary) {
    if (itinerary == null) itinerary = "Main";
    var timeDiff = newStartDate.diff(oldStartDate);
    let op_events = [];
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary !== itinerary) return;
      let startDate = DateTime.fromISO(event.Start, { zone: "utc" });
      if (startDate >= oldStartDate) {
        op_events.push(event);
      }
    });
    op_events.forEach((event) => {
      const index = this.events.findIndex((x) => x.ID === event.ID);
      if (index === -1) return;
      this.events[index].Start = DateTime.fromISO(this.events[index].Start, { zone: "utc" })
        .plus({ seconds: timeDiff / 1000 })
        .toISO({ zone: "utc" });
      this.events[index].End = DateTime.fromISO(this.events[index].End, { zone: "utc" })
        .plus({ seconds: timeDiff / 1000 })
        .toISO({ zone: "utc" });
    });
    //console.log(this.events);
  }

  reorderEventsOLD(fromEvent, toEvent, itinerary) {
    if (itinerary == null) itinerary = "Main";
    let sortedEvents = [];
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary !== itinerary) return;
      sortedEvents.push(event);
    });
    sortedEvents = sortedEvents.sort((a, b) =>
      new Date(a.Start).getTime() < new Date(b.Start).getTime()
        ? -1
        : new Date(a.Start).getTime() > new Date(b.Start).getTime()
        ? 1
        : 0
    );
    const fromIndex = sortedEvents.findIndex((x) => x.ID === fromEvent.ID);
    const toIndex = sortedEvents.findIndex((x) => x.ID === toEvent.ID);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;
    let sooner = toIndex <= fromIndex ? true : false;
    var timeDiff = DateTime.fromISO(fromEvent.End).diff(DateTime.fromISO(fromEvent.Start));
    if (sooner) {
      sortedEvents[fromIndex].Start = DateTime.fromISO(sortedEvents[toIndex].Start, { zone: "utc" });
      sortedEvents[fromIndex].End = DateTime.fromISO(sortedEvents[toIndex].Start, { zone: "utc" })
        .plus({ seconds: timeDiff / 1000 })
        .toISO({ zone: "utc" });
      for (let x = toIndex; x < fromIndex; x++) {
        let calc = true;
        if (x !== 0 && x > toIndex + 1 && sortedEvents[x].Start >= sortedEvents[x - 1].End) calc = false;
        if (calc) {
          sortedEvents[x].Start = DateTime.fromISO(sortedEvents[x].Start, { zone: "utc" })
            .plus({ seconds: timeDiff / 1000 })
            .toISO({ zone: "utc" });
          sortedEvents[x].End = DateTime.fromISO(sortedEvents[x].End, { zone: "utc" })
            .plus({ seconds: timeDiff / 1000 })
            .toISO({ zone: "utc" });
        }
      }
    } else {
      sortedEvents[fromIndex].End = DateTime.fromISO(sortedEvents[toIndex].End, { zone: "utc" });
      sortedEvents[fromIndex].Start = DateTime.fromISO(sortedEvents[toIndex].End, { zone: "utc" })
        .minus({ seconds: timeDiff / 1000 })
        .toISO({ zone: "utc" });
    }
    for (let x = fromIndex; x < toIndex; x++) {
      let calc = true;
      if (x !== 0 && x > toIndex - 1 && sortedEvents[x].Start >= sortedEvents[x - 1].End) calc = false;
      if (calc) {
        sortedEvents[x].Start = DateTime.fromISO(sortedEvents[x].Start, { zone: "utc" })
          .minus({ seconds: timeDiff / 1000 })
          .toISO({ zone: "utc" });
        sortedEvents[x].End = DateTime.fromISO(sortedEvents[x].End, { zone: "utc" })
          .minus({ seconds: timeDiff / 1000 })
          .toISO({ zone: "utc" });
      }
    }
  }

  reorderEvents(fromEvent, toEvent, itinerary) {
    if (typeof fromEvent === "undefined" || typeof toEvent === "undefined") return;
    if (itinerary == null) itinerary = "Main";
    let sortedEvents = [];
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary !== itinerary) return;
      sortedEvents.push(event);
    });
    sortedEvents = sortedEvents.sort((a, b) =>
      new Date(a.Start).getTime() < new Date(b.Start).getTime()
        ? -1
        : new Date(a.Start).getTime() > new Date(b.Start).getTime()
        ? 1
        : 0
    );
    const fromIndex = sortedEvents.findIndex((x) => x.ID === fromEvent.ID);
    const toIndex = sortedEvents.findIndex((x) => x.ID === toEvent.ID);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;
    const move_to_later = toIndex > fromIndex;
    var fromEventDuration = DateTime.fromISO(fromEvent.End).diff(DateTime.fromISO(fromEvent.Start));

    sortedEvents[fromIndex].Start = DateTime.fromISO(sortedEvents[toIndex].End, { zone: "utc" });
    sortedEvents[fromIndex].End = DateTime.fromISO(sortedEvents[toIndex].End, { zone: "utc" })
      .plus({ seconds: fromEventDuration / 1000 })
      .toISO({ zone: "utc" });
    //if (move_to_later) {
    //console.log("*****move_to_later");
    for (let x = toIndex + 1; x < sortedEvents.length - 1; x++) {
      if (x === fromIndex) continue;
      //console.log("*****day", sortedEvents[x].Start, sortedEvents[toIndex].Start.day);
      if (DateTime.fromISO(sortedEvents[x].Start, { zone: "utc" }).day !== DateTime.fromISO(sortedEvents[toIndex].Start, { zone: "utc" }).day) continue;
      sortedEvents[x].Start = DateTime.fromISO(sortedEvents[x].Start, { zone: "utc" })
        .plus({ seconds: fromEventDuration / 1000 })
        .toISO({ zone: "utc" });
      sortedEvents[x].End = DateTime.fromISO(sortedEvents[x].End, { zone: "utc" })
        .plus({ seconds: fromEventDuration / 1000 })
        .toISO({ zone: "utc" });
    }
    //} //else {
    // for (let x = toIndex + 1; x < sortedEvents.length-1; x++) {
    //   if(sortedEvents[x].Start.day !== sortedEvents[toIndex].day) continue;
    //     sortedEvents[x].Start = DateTime.fromISO(sortedEvents[x].Start, { zone: "utc" })
    //       .plus({ seconds: fromEventDuration / 1000 })
    //       .toISO({ zone: "utc" });
    //     sortedEvents[x].End = DateTime.fromISO(sortedEvents[x].End, { zone: "utc" })
    //       .plus({ seconds: fromEventDuration / 1000 })
    //       .toISO({ zone: "utc" });
    // }
  }

  reorderEventsNew(drag_event, drop_event, itinerary) {
    if (typeof drag_event === "undefined" || typeof drop_event === "undefined") return;
    if (drag_event.ID === drop_event.ID) return;
    if (itinerary == null) itinerary = "Main";

    let sortedEvents = [];
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary !== itinerary) return;
      if (event.ID === drag_event.ID) return;
      sortedEvents.push(event);
    });
    sortedEvents = sortedEvents.sort((a, b) =>
      new Date(a.Start).getTime() < new Date(b.Start).getTime()
        ? -1
        : new Date(a.Start).getTime() > new Date(b.Start).getTime()
        ? 1
        : 0
    );
    const toIndex = sortedEvents.findIndex((x) => x.ID === drop_event.ID);
    if (toIndex === -1) return;

    var duration_of_drag_event = DateTime.fromISO(drag_event.End).diff(DateTime.fromISO(drag_event.Start));

    for (let x = toIndex + 1; x < sortedEvents.length; x++) {
      sortedEvents[x].Start = DateTime.fromISO(sortedEvents[x].Start, { zone: "utc" })
        .plus({ seconds: duration_of_drag_event / 1000 })
        .toISO({ zone: "utc" });
      sortedEvents[x].End = DateTime.fromISO(sortedEvents[x].End, { zone: "utc" })
        .plus({ seconds: duration_of_drag_event / 1000 })
        .toISO({ zone: "utc" });
    }
    drag_event.Start = sortedEvents[toIndex].End;
    drag_event.End = DateTime.fromISO(drag_event.Start, { zone: "utc" })
      .plus({ seconds: duration_of_drag_event / 1000 })
      .toISO({ zone: "utc" });
  }

  recalculateScheduleOLD(itinerary, event_time_rounding, event_default_duration, _Places) {
    if (this.events.length === 0) return;
    if (itinerary == null) itinerary = "Main";
    let sortedEvents = [];
    let rounding_to = event_time_rounding;
    if (typeof rounding_to === "undefined") rounding_to = 15;
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary !== itinerary) return;
      sortedEvents.push(event);
    });
    sortedEvents = sortedEvents.sort((a, b) =>
      new Date(a.Start).getTime() < new Date(b.Start).getTime()
        ? -1
        : new Date(a.Start).getTime() > new Date(b.Start).getTime()
        ? 1
        : 0
    );
    let travel_time = 0;
    let last_place = null;
    let cur_place = null;
    let dayDate = DateTime.fromISO(sortedEvents[0].Start);
    let thisDay = dayDate;
    let firstEventOfDay = true;
    for (let x = 0; x < sortedEvents.length; x++) {
      if (dayDate.day !== thisDay.day) {
        thisDay = dayDate;
        firstEventOfDay = true;
      } else firstEventOfDay = false;
      cur_place = null;
      const event = sortedEvents[x];
      let duration = 0;
      if (event.Places !== null && event.Places !== "") {
        let place_ids = event.Places.split(",");
        for (let p = 0; p < place_ids.length; p++) {
          const place = _Places.getPlace(place_ids[p]);
          if (place != null) {
            duration += place.Duration;
            cur_place = place;
          }
        }
        if (duration === 0) duration = event_default_duration;
      }
      travel_time = 0;
      if (x > 0 && x < sortedEvents.length && last_place !== null && cur_place != null) {
        travel_time = Common.estimateTravelTimeMinutes(last_place, cur_place, rounding_to);
      }
      if (cur_place !== null) last_place = cur_place;
      if (dayDate.hour + travel_time / 60 + duration / 60 > 18) {
        dayDate = dayDate.plus({ hours: 24 });
        dayDate = dayDate.set({ hour: 11 });
        dayDate = dayDate.set({ minutes: 0 });
      }
      sortedEvents[x].Start = dayDate.toISO();
      if (!firstEventOfDay || travel_time > 30) dayDate = dayDate.plus({ minutes: travel_time });
      //event.Start = dayDate.toISO();
      dayDate = dayDate.plus({ minutes: duration });
      sortedEvents[x].End = dayDate.toISO();
      if (dayDate.hour >= 17) {
        dayDate = dayDate.plus({ hours: 24 });
        dayDate = dayDate.set({ hour: 11 });
        dayDate = dayDate.set({ minutes: 0 });
      }
    }
  }

  // max_allowed_date (optional, default null = no cap, existing behavior
  // unchanged): the first calendar day OUTSIDE the requested window — any
  // event that would land on or after it is deleted rather than rolled into
  // an extra day. Only TripAI.js's parseSched passes this (honoring the
  // trip's actual requested day count over always fitting every AI-
  // generated item) — the manual "Recalculate Schedule" menu action leaves
  // it null, so a user re-timing their own hand-built schedule never has
  // events silently deleted. Matches ScheduleBuilder.js's
  // buildOpeningHoursAwareSchedule's identical dayCap/maxAllowedDate
  // philosophy (leave excess unscheduled rather than inflate the trip).
  // Found 2026-09-24.
  // pace_multiplier (optional, default 1 = no change, matching every
  // existing caller/test that doesn't pass it): scales each event's
  // computed duration, same knob as BuildMyTripWizard's
  // addEventOpeningHoursAware Pace setting — lets a user re-time an
  // already-built schedule to feel more relaxed or more packed without
  // re-searching for places. Applies whether the duration source is
  // Place.Duration or the event's own existing Start/End span, so
  // "Schedule" duration mode genuinely stretches/compresses each event's
  // block, not just the gaps between them. Found 2026-09-30.
  recalculateSchedule(itinerary, event_time_rounding, event_default_duration, _Places, geography, day_start_hour, day_end_hour, use_place_duration, avoid_closed, max_allowed_date, pace_multiplier) {
    if (this.events.length === 0) return;
    if (itinerary == null) itinerary = "Main";
    if (day_start_hour == null) day_start_hour = 9;
    if (day_end_hour == null) day_end_hour = 20;
    // Defaults to preserving each event's own existing Start/End span
    // (visible right in the schedule) rather than silently overwriting it
    // with Place.Duration — a field many users never touch or even know
    // exists. A schedule-set duration is one-way: recalculating over it
    // with use_place_duration=true loses it for good, whereas a place's own
    // Duration can always be pulled in later by explicitly opting in.
    if (use_place_duration == null) use_place_duration = false;
    if (avoid_closed == null) avoid_closed = true;
    if (pace_multiplier == null) pace_multiplier = 1;
    let sortedEvents = [];
    let rounding_to = event_time_rounding;
    if (typeof rounding_to === "undefined") rounding_to = 15;
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary !== itinerary) return;
      sortedEvents.push(event);
    });
    if (geography === true) {
      // Group events by calendar day so we optimize within each day, not globally
      const dayMap = new Map();
      for (const event of sortedEvents) {
        const dayKey = DateTime.fromISO(event.Start, { zone: "utc" }).toISODate();
        if (!dayMap.has(dayKey)) dayMap.set(dayKey, { withPlaces: [], withoutPlaces: [] });
        const grp = dayMap.get(dayKey);
        if (event.Places && event.Places.trim() !== "") {
          const place = _Places.getPlace(event.Places.split(",")[0].trim());
          if (place) grp.withPlaces.push({ event, place });
          else grp.withoutPlaces.push(event);
        } else {
          grp.withoutPlaces.push(event);
        }
      }
      sortedEvents = [];
      for (const dayKey of [...dayMap.keys()].sort()) {
        const grp = dayMap.get(dayKey);
        if (grp.withPlaces.length > 1) {
          let dayStartForOrder = DateTime.fromISO(dayKey + "T00:00:00.000Z", { zone: "utc" }).plus({ hours: day_start_hour });
          const ordered = avoid_closed
            ? orderPlacesAvoidingClosed(grp.withPlaces, dayStartForOrder, rounding_to, use_place_duration, event_default_duration)
            : Common.getPlaceOrderByDistance(null, window.guide_obj, grp.withPlaces.map(x => x.place))
                .map(p => grp.withPlaces.find(x => x.place.ID === p.ID))
                .filter(Boolean);
          // A place's best time of day (Place.BestTime, from Create Trip
          // w/AI) wins over distance: "morning" places first, "sunset"/
          // "evening" ones last, the rest in the order found above (a stable
          // sort). 2026-10-16.
          const rank = (x) => (x.place.BestTime === "morning" ? 0 : x.place.BestTime === "sunset" || x.place.BestTime === "evening" ? 2 : 1);
          sortedEvents.push(...[...ordered].sort((a, b) => rank(a) - rank(b)).map(x => x.event));
        } else {
          sortedEvents.push(...grp.withPlaces.map(x => x.event));
        }
        sortedEvents.push(...grp.withoutPlaces);
      }
    } else {
      sortedEvents = sortedEvents.sort((a, b) =>
        new Date(a.Start).getTime() < new Date(b.Start).getTime()
          ? -1
          : new Date(a.Start).getTime() > new Date(b.Start).getTime()
          ? 1
          : 0
      );
    }
    if (sortedEvents.length === 0) return;
    let travel_time = 0;
    let last_place = null;
    let cur_place = null;
    let dayDate;
    // day_start_hour is now a true anchor in both directions — shifting it
    // earlier (e.g. 9am -> 6am) or later (9am -> 11am) always moves the
    // day's events accordingly. It used to be a one-directional floor when
    // geography===false: it would push a day later if the existing first
    // event started before it, but never pull an already-later start
    // earlier — the same "Start Hour" control in the dialog behaved
    // completely differently depending on the (separate) reorder toggle,
    // which looked like broken shifting rather than a deliberate floor.
    {
      const firstDayKey = DateTime.fromISO(sortedEvents[0].Start, { zone: "utc" }).toISODate();
      dayDate = DateTime.fromISO(firstDayKey + "T00:00:00.000Z", { zone: "utc" }).plus({ hours: day_start_hour });
    }
    let firstEventOfDay = true;
    for (let x = 0; x < sortedEvents.length; x++) {
      const event = sortedEvents[x];
      const eventDayKey = DateTime.fromISO(event.Start, { zone: "utc" }).toISODate();
      // Compares against dayDate's OWN current date, not a separately
      // tracked "last event's day" bookkeeping variable — that variable
      // used to record each event's original, pre-rollover day, so once an
      // overflow rollover pushed dayDate forward past a day boundary (e.g.
      // a packed day's last stop rolling into the next morning), the very
      // next array event — still nominally dated the day just rolled past
      // — looked like a fresh day boundary and reset dayDate BACKWARD to
      // that day's start, re-colliding with the event that had just rolled
      // there, and could oscillate back and forth for the rest of the
      // list. Only ever jumping dayDate FORWARD (never backward) to a
      // genuinely later day fixes this: an event whose nominal day has
      // already been passed just continues from wherever dayDate is.
      // Found 2026-09-29 via a real Venice (1 day) + Milan (1 day) AI trip
      // (a heavily overflow-triggering test case) that still overlapped
      // and reordered Milan's events after the first, narrower fix above.
      //
      // event.IsTravel (a real train/flight, set by TripAI.js's parseSched
      // from the AI's own IsTravel label) does NOT get any special
      // fixed-anchor treatment here — several attempts at that (never
      // repositioning it, shifting it forward to dodge a collision,
      // dropping competing content ahead of it) each fixed one real bug but
      // exposed another (a cascading day-shift that crossed max_allowed_date
      // and silently deleted the rest of the trip; a "next morning" event
      // getting glued onto the same evening as an evening arrival). Given
      // how fragile that got, IsTravel events are now positioned by this
      // exact same general logic as any other event (a meal, an attraction)
      // — the one thing kept from that whole effort is resetting last_place
      // after one (below), since without that the next real place's
      // travel_time was computed as a bogus cross-city distance from
      // whatever place came before the travel leg, as if it never happened.
      // Found/simplified 2026-09-30.
      if (x === 0) {
        firstEventOfDay = true;
      } else if (eventDayKey > dayDate.toISODate()) {
        dayDate = DateTime.fromISO(eventDayKey + "T00:00:00.000Z", { zone: "utc" }).plus({ hours: day_start_hour });
        firstEventOfDay = true;
      } else {
        firstEventOfDay = false;
      }

      cur_place = null;
      let duration = 0;
      if (event.Places !== null && event.Places !== "") {
        let place_ids = event.Places.split(",");
        for (let p = 0; p < place_ids.length; p++) {
          const place = _Places.getPlace(place_ids[p]);
          if (place != null) {
            if (use_place_duration) duration += place.Duration;
            cur_place = place;
          }
        }
      }
      if (use_place_duration) {
        if (duration === 0) duration = event_default_duration;
      } else {
        duration = Math.round(
          DateTime.fromISO(event.End, { zone: "utc" })
            .diff(DateTime.fromISO(event.Start, { zone: "utc" }), "minutes").minutes
        );
        if (duration <= 0) duration = event_default_duration;
      }
      duration = Math.round(duration * pace_multiplier);
      travel_time = 0;
      if (x > 0 && x < sortedEvents.length && last_place !== null && cur_place != null) {
        travel_time = Common.estimateTravelTimeMinutes(last_place, cur_place, rounding_to);
      }
      if (cur_place !== null) last_place = cur_place;
      // A travel-leg event has no linked Place (cur_place stays null for
      // it), so without this the place before it stayed "last known" across
      // the whole trip — the next real place's travel_time then got
      // computed as a bogus cross-city distance from it, as if the trip to
      // the new city never happened. This is the one piece kept from a
      // larger, more fragile fixed-anchor attempt at this event type — see
      // this loop's own top-of-iteration comment. Found 2026-09-30.
      if (event.IsTravel === true) last_place = null;
      // Same guard as the actual dayDate advancement below (!firstEventOfDay)
      // — travel_time is never charged to a first-of-day event, so this
      // overflow check must not count it either. Without this, last_place
      // held over from the previous (different city's) day made the very
      // first event of a new city's day see a huge cross-city travel_time
      // here, spuriously overflowing it into a fabricated extra day even
      // though that travel_time would never actually be added to dayDate.
      // Found 2026-09-29 via a real Milan (1 day) + Florence (1 day) AI trip
      // that came out with a stray Milan attraction and extra transition
      // entries. Matches the related fix just above for the same root cause
      // (a different symptom of it).
      if (dayDate.hour + (firstEventOfDay ? 0 : travel_time / 60) + duration / 60 > day_end_hour) {
        dayDate = dayDate.startOf("day").plus({ days: 1, hours: day_start_hour });
        // This event is now effectively anchoring a fresh day (dayDate just
        // jumped to day_start_hour on a new date) — treat it as one, same as
        // the eventDayKey-based reset above, so travel_time doesn't also get
        // tacked on right after the jump. Without this, an already-drifted
        // dayDate could keep drifting event over event, and — since dayDate
        // only ever moves forward — permanently desync from every
        // subsequent event's own nominal Date, silently swallowing later
        // real day boundaries for the rest of the schedule. Found
        // 2026-09-30 alongside the IsTravel fixed-anchor fix above, which is
        // what actually protects a real travel leg from this drift.
        firstEventOfDay = true;
      }
      // Travel time is spent arriving at this event, so advance the cursor
      // by it before setting Start — the gap shows up between the previous
      // event's End and this one (matching BuildMyTripWizard's convention)
      // instead of silently stretching the previous event's own block to
      // cover time that wasn't actually spent there. Never applied on the
      // first event of a day (even a huge inter-city travel_time) — matches
      // ScheduleBuilder.js's addEventOpeningHoursAware, which treats a fresh
      // day as "you're already here," not something to tax for how you got
      // there. Previously had a "|| travel_time > 30" override that broke
      // exactly this for multi-city AI trips: crossing from Rome to Paris on
      // a new day would tack Rome->Paris's full travel estimate onto Paris's
      // day-start time, delaying every event and spilling into an extra day.
      // Found 2026-09-23 via a real Build Trip with AI test.
      if (!firstEventOfDay) dayDate = dayDate.plus({ minutes: travel_time });
      // Round to the user's event_time_rounding increment so auto-scheduled
      // events land on clean clock times (12:15) instead of the leftover
      // fractional-minute drift (12:14) from accumulating travel-time/
      // duration minutes with no rounding step. Rounds the running cursor
      // itself, not just the displayed value, so the next event's travel
      // time/duration accumulate from the rounded point forward.
      //
      // roundDateTimeToNearestMinutes always rounds UP (Math.ceil), never
      // back down — needed so a rounded Start can never land before the
      // actual cursor (which would overlap the previous event). But that
      // means a large rounding_to (e.g. a 30-minute preference) could
      // inflate a genuinely short travel gap into the full increment: a
      // previous End already aligned to 10:00 plus a real 5-minute walk
      // (cursor at 10:05) ceiling-rounds to 10:30 — a 30-minute gap for a
      // 5-minute walk. Capped at 15 here, specifically for the
      // travel-time-driven advance only (not the duration-driven one right
      // below, which still uses the user's full preference so an
      // intentionally clean event length isn't affected) — bounds the
      // worst-case inflation without ignoring the user's rounding
      // preference entirely. User-reported/found 2026-09-30.
      const startRoundingTo = Math.min(rounding_to, 15);
      dayDate = Common.roundDateTimeToNearestMinutes(dayDate, startRoundingTo);
      sortedEvents[x].Start = dayDate.toISO();
      dayDate = dayDate.plus({ minutes: duration });
      dayDate = Common.roundDateTimeToNearestMinutes(dayDate, rounding_to);
      sortedEvents[x].End = dayDate.toISO();
      if (dayDate.hour >= day_end_hour) {
        dayDate = dayDate.startOf("day").plus({ days: 1, hours: day_start_hour });
      }
      // Drop rather than keep once placed on/after the cap — see this
      // method's own max_allowed_date comment above. Checked last, using
      // the Start just written, so it plays back nicely with everything
      // above (travel_time/last_place bookkeeping already happened) —
      // dayDate only ever moves forward, so once one event crosses the cap
      // every event after it does too, this never "un-crosses".
      if (max_allowed_date && DateTime.fromISO(event.Start, { zone: "utc" }).startOf("day") >= max_allowed_date) {
        this.deleteEvent(event);
      }
    }
  }

  // pace_multiplier — see the matching comment on recalculateSchedule.
  recalculateDay(dayISODate, itinerary, event_time_rounding, event_default_duration, _Places, geography, day_start_hour, use_place_duration, avoid_closed, pace_multiplier, day_end_hour) {
    if (this.events.length === 0) return;
    if (itinerary == null) itinerary = "Main";
    if (day_start_hour == null) day_start_hour = 9;
    // See the matching comment in recalculateSchedule — defaults to
    // preserving each event's own existing duration rather than silently
    // overwriting it with Place.Duration.
    if (use_place_duration == null) use_place_duration = false;
    if (avoid_closed == null) avoid_closed = true;
    if (pace_multiplier == null) pace_multiplier = 1;
    let rounding_to = typeof event_time_rounding !== "undefined" ? event_time_rounding : 15;

    let dayEvents = this.events.filter((event) => {
      const _itinerary = typeof event.Itinerary === "undefined" ? "Main" : event.Itinerary;
      if (_itinerary !== itinerary) return false;
      return DateTime.fromISO(event.Start, { zone: "utc" }).toISODate() === dayISODate;
    });
    if (dayEvents.length === 0) return;

    if (geography === true) {
      const withPlaces = [];
      const withoutPlaces = [];
      for (const event of dayEvents) {
        if (event.Places && event.Places.trim() !== "") {
          const place = _Places.getPlace(event.Places.split(",")[0].trim());
          if (place) withPlaces.push({ event, place });
          else withoutPlaces.push(event);
        } else {
          withoutPlaces.push(event);
        }
      }
      if (withPlaces.length > 1) {
        let dayStartForOrder = DateTime.fromISO(dayISODate + "T00:00:00.000Z", { zone: "utc" }).plus({ hours: day_start_hour });
        const ordered = avoid_closed
          ? orderPlacesAvoidingClosed(withPlaces, dayStartForOrder, rounding_to, use_place_duration, event_default_duration)
          : Common.getPlaceOrderByDistance(null, window.guide_obj, withPlaces.map(x => x.place))
              .map(p => withPlaces.find(x => x.place.ID === p.ID))
              .filter(Boolean);
        dayEvents = ordered.map(x => x.event);
      } else {
        dayEvents = withPlaces.map(x => x.event);
      }
      dayEvents.push(...withoutPlaces);
    } else {
      dayEvents = dayEvents.sort((a, b) => new Date(a.Start) - new Date(b.Start));
    }

    // day_start_hour is a true anchor regardless of geography — it used to
    // be a one-directional floor when geography===false (only pushed the
    // day later if the existing first event started before it, never
    // pulled an already-later start earlier), which made the same "Start
    // Hour" dialog control behave inconsistently depending on the separate
    // reorder toggle.
    let dayDate = DateTime.fromISO(dayISODate + "T00:00:00.000Z", { zone: "utc" }).plus({ hours: day_start_hour });
    // This function recalculates only dayISODate's events — unlike
    // recalculateSchedule (which legitimately spans multiple days and rolls
    // overflow to the next date's day_start_hour), there's no "next day" for
    // a single-day recalculation to roll into: doing so would silently date
    // an event outside the day the caller asked for, and a later
    // recalculateDay("dayISODate", ...) call wouldn't even find it anymore
    // (the filter above matches by exact date). So instead of capping to a
    // day_end_hour, a packed day is simply allowed to run late — up to
    // midnight. A stop that can't finish by then used to be clamped to
    // 23:59–23:59 (zero minutes long, found 2026-10-28 in Osaka 3 days);
    // now it and the stops after it keep their length and go to the next
    // day, after that day's own stops. day_end_hour (optional, the
    // Recalculate dialog's End hour) moves the limit earlier: a stop that
    // would end after it moves too, as recalculateSchedule does — the
    // dialog used to ask for an End hour that only a whole-schedule
    // recalculation obeyed. Returns { movedToNextDay: [subjects] } so the
    // caller can say so.
    const dayEndBoundary = day_end_hour != null && day_end_hour < 24
      ? DateTime.fromISO(dayISODate + "T00:00:00.000Z", { zone: "utc" }).plus({ hours: day_end_hour })
      : DateTime.fromISO(dayISODate + "T23:59:00.000Z", { zone: "utc" });
    const nextDayISO = DateTime.fromISO(dayISODate, { zone: "utc" }).plus({ days: 1 }).toISODate();
    const durationOf = (event) => {
      let duration = 0;
      if (use_place_duration && event.Places) {
        for (const pid of event.Places.split(",")) {
          const place = _Places.getPlace(pid.trim());
          if (place) duration += place.Duration;
        }
      }
      if (!use_place_duration) {
        duration = Math.round(
          DateTime.fromISO(event.End, { zone: "utc" })
            .diff(DateTime.fromISO(event.Start, { zone: "utc" }), "minutes").minutes
        );
      }
      if (!(duration > 0)) duration = event_default_duration;
      return Math.round(duration * pace_multiplier);
    };
    const placeOf = (event) => {
      let cur_place = null;
      for (const pid of (event.Places || "").split(",")) {
        const place = pid.trim() ? _Places.getPlace(pid.trim()) : null;
        if (place) cur_place = place;
      }
      return cur_place;
    };
    // Lays the events out one after another from cursor; stops before the
    // first one that would end past `boundary` and returns its index.
    const layOut = (events, cursor, last_place, firstEvent, boundary) => {
      for (let x = 0; x < events.length; x++) {
        const event = events[x];
        const duration = durationOf(event);
        const cur_place = placeOf(event);
        let travel_time = 0;
        if (!firstEvent && last_place !== null && cur_place !== null) {
          travel_time = Common.estimateTravelTimeMinutes(last_place, cur_place, rounding_to);
        }
        // Travel time is spent arriving at this event, so advance the cursor
        // by it before setting Start — see the matching comment in
        // recalculateSchedule's loop above.
        let start = cursor;
        if (!firstEvent || travel_time > 30) start = start.plus({ minutes: travel_time });
        // Capped at 15 here — see recalculateSchedule's matching comment
        // (roundDateTimeToNearestMinutes always rounds UP, so a large
        // rounding_to can inflate a short travel gap into the full
        // increment). Only the travel-driven advance above is capped; the
        // duration-driven one below still uses the user's full preference.
        start = Common.roundDateTimeToNearestMinutes(start, Math.min(rounding_to, 15));
        const end = Common.roundDateTimeToNearestMinutes(start.plus({ minutes: duration }), rounding_to);
        if (boundary && end > boundary) return x;
        event.Start = start.toISO();
        event.End = end.toISO();
        cursor = end;
        if (cur_place !== null) last_place = cur_place;
        firstEvent = false;
      }
      return events.length;
    };
    const dayStart = DateTime.fromISO(dayISODate + "T00:00:00.000Z", { zone: "utc" }).plus({ hours: day_start_hour });
    let fitted = layOut(dayEvents, dayStart, null, true, dayEndBoundary);
    if (fitted === 0) fitted = 1; // a single stop longer than the day stays, rather than moving forever
    if (fitted < dayEvents.length) layOut(dayEvents.slice(0, fitted), dayStart, null, true, null);
    const overflow = dayEvents.slice(fitted);
    if (overflow.length > 0) {
      const nextDayEvents = this.events.filter((e) =>
        (typeof e.Itinerary === "undefined" ? "Main" : e.Itinerary) === itinerary &&
        !overflow.includes(e) &&
        DateTime.fromISO(e.Start, { zone: "utc" }).toISODate() === nextDayISO
      );
      let cursor = DateTime.fromISO(nextDayISO + "T00:00:00.000Z", { zone: "utc" }).plus({ hours: day_start_hour });
      let last_place = null;
      for (const e of nextDayEvents) {
        const end = DateTime.fromISO(e.End, { zone: "utc" });
        if (end > cursor) {
          cursor = end;
          last_place = placeOf(e) || last_place;
        }
      }
      layOut(overflow, cursor, last_place, nextDayEvents.length === 0, null);
    }
    return { movedToNextDay: overflow.map((e) => e.Subject || "Event") };
  }

  reorderDays(
    drag_day,
    day_before_drag_day,
    day_after_drag_day,
    drop_day,
    day_after_drop_day,
    itinerary,
    drop_is_last
  ) {
    //if (DateTime.fromISO(drag_day.date) === DateTime.fromISO(drop_day.date)) return;
    if (
      drag_day.date.day === drop_day.date.day &&
      drag_day.date.month === drop_day.date.month &&
      drag_day.date.year === drop_day.date.year
    )
      return;
    if (
      drag_day.date.day === drop_day.date.day + 1 &&
      drag_day.date.month === drop_day.date.month &&
      drag_day.date.year === drop_day.date.year
    )
      return;
    let gap_day = null;
    //let after_delta;
    //If day_after_drop_day is more than 1 day more than drop_day or drop_day is last_day
    //- move all events in drag_day to drop_day + 1 day)
    var drop_day_plus_1 = drop_day.date.plus({ days: 1 });

    var day_after_drop_day_plus_1 = day_after_drop_day.date.plus({ days: 1 });

    var drop_day_minus_1 = drop_day.date.minus({ days: 1 });

    var day_after_drag_day_minus_1 = day_after_drag_day.date.minus({ days: 1 });

    //let day_diff = day_after_drop_day.date.diff(drop_day.date, "days").days;
    //let more_than_one_day = day_after_drop_day.date.day - drop_day.date.day > 1;

    if ((day_after_drop_day !== null && day_after_drop_day.date.day - drop_day.date.day > 1) || drop_is_last) {
      this.shiftDayEvents(drag_day, drop_day_plus_1, itinerary, { ID: -1 }, true);
    } else {
      // - move all events from drag_day to some earlier year
      // - shift all events in days after drop_day by +1 day
      // - move all events in drag_day (pereviously moved to earlier year) to drop_day + 1 day)
      // - if any day after drop_day is more than 1 day from previous day
      //   - shift all days starting from gap_day by -1 day
      let save_date = drag_day.date;
      save_date = save_date.minus({ years: 100 });
      this.shiftDayEvents(drag_day.date, save_date, itinerary, { ID: -1 }, true);
      this.moveAllDays(day_after_drop_day.date, day_after_drop_day_plus_1, itinerary, drag_day.date);
      this.shiftDayEvents(save_date, drop_day_plus_1, itinerary, { ID: -1 }, true);
    }
    //   if drag_day < drop_day
    // - shift all days starting from day_before_drag_day by -1 day
    if (DateTime.fromISO(drag_day.date) < DateTime.fromISO(drop_day.date)) {
      this.moveAllDays(drop_day.date, drop_day_minus_1, itinerary);
    }
    if (DateTime.fromISO(drag_day.date) > DateTime.fromISO(drop_day.date)) {
      this.moveAllDays(day_after_drag_day.date, day_after_drag_day_minus_1, itinerary);
    }
  }

  moveAllDays(oldStartDate, newStartDate, itinerary, except) {
    if (itinerary == null) itinerary = "Main";
    var timeDiff = newStartDate.diff(oldStartDate);
    //console.log("moveAllDays", oldStartDate, newStartDate, timeDiff);
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary !== itinerary) return;
      let startDate = DateTime.fromISO(event.Start, { zone: "utc" });
      if (except !== null && typeof except !== "undefined") {
        if (startDate.year === except.year && startDate.month === except.month && startDate.day === except.day) return;
      }
      if (
        startDate.year >= oldStartDate.year &&
        startDate.month >= oldStartDate.month &&
        startDate.day >= oldStartDate.day
      ) {
        event.Start = DateTime.fromISO(event.Start, { zone: "utc" })
          .plus({ seconds: timeDiff / 1000 })
          .toISO({ zone: "utc" });
        event.End = DateTime.fromISO(event.End, { zone: "utc" })
          .plus({ seconds: timeDiff / 1000 })
          .toISO({ zone: "utc" });
      }
    });
    //console.log(this.events);
  }

  deleteDay(deleteDate, itinerary) {
    //console.log("deleteDay", deleteDate, itinerary);
    if (itinerary == null) itinerary = "Main";
    let new_events = [];
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      // if (_itinerary !== itinerary) return;
      let startDate = DateTime.fromISO(event.Start, { zone: "utc" });
      if (
        startDate.year !== deleteDate.year ||
        startDate.month !== deleteDate.month ||
        startDate.day !== deleteDate.day ||
        event.Itinerary !== itinerary
      ) {
        new_events.push(event);
      }
    });
    //console.log("deleteDay", new_events);
    this.events = new_events;
  }

  getScheduleToDisplay2(_EventSearchText, t_scheduleName, _Places) {
    let EventSearchText = (_EventSearchText || "").toUpperCase();
    let scheduleName = t_scheduleName;
    //console.log("getScheduleToDisplay2", EventSearchText, scheduleName);
    if (scheduleName == null) scheduleName = "Main";
    scheduleName = scheduleName.replace(/%20/g, " ");

    //console.log(this.events);
    let sortedEvents = this.events.sort((a, b) =>
      new Date(a.Start).getTime() < new Date(b.Start).getTime()
        ? -1
        : new Date(a.Start).getTime() > new Date(b.Start).getTime()
        ? 1
        : 0
    );
    //console.log("sortedEvents", sortedEvents);
    let day = -1;
    let dayNum = 0;
    let schedule = [];
    let new_date = null;
    let order_index = 1;
    for (let i = 0; i < sortedEvents.length; i++) {
      let event = sortedEvents[i];
      let _scheduleName = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      _scheduleName = _scheduleName.replace(/%20/g, " ");
      if (_scheduleName !== scheduleName) continue;
      if (EventSearchText !== "") {
        // the event's title or the name of a place it visits (2026-10-28)
        if (!eventMatchesSearch(event, EventSearchText, _Places)) continue;
      }
      //console.log("getScheduleToDisplay2 event.Start", event.Start, _scheduleName, sortedEvents[i].Subject);

      let startDate = DateTime.fromISO(event.Start, { zone: "utc" });

      // console.log(
      //   "getScheduleToDisplay2 startDate: ",
      //   startDate,
      //   "event.Start: ",
      //   event.Start,
      //   "Common.formatDate(startDate): ",
      //   Common.formatDate(startDate)
      // );
      if (day !== startDate.day) {
        // New date found
        if (new_date != null) schedule.push(new_date);
        new_date = { Title: Common.formatDateLuxon(startDate), date: startDate, data: [], index: (i + 1).toString() };
        day = startDate.day;
        dayNum += 1;
      }
      event.dayNum = dayNum;
      if (new_date != null) {
        if (event.Places !== null && event.Places !== "" && typeof document !== "undefined") {
          let place_ids = event.Places.split(",");
          let num_places = 0;
          for (let p = 0; p < place_ids.length; p++) {
            const place = _Places.getPlace(place_ids[p]);
            if (place != null) {
              //if (place.PType === "Icon")
              num_places++;
              // City for the day's header — taken from the first resolvable
              // place of the day's first event, so a multi-destination trip
              // (e.g. "Build My Trip" across several cities) shows which
              // destination each day belongs to instead of just a bare date,
              // which reads as one long undifferentiated list otherwise.
              if (!new_date.City && place.City) new_date.City = place.City;
            }
          }
          event.order_index = order_index.toString();
          order_index = order_index + num_places;
        }
        new_date.data.push(event);
      }
    }
    if (new_date != null) schedule.push(new_date);
    //console.log(schedule);
    return schedule;
  }

  getScheduleItems(EventSearchText, itinerary, _Places) {
    //console.log("getScheduleItems", itinerary);
    return this.getScheduleToDisplay2(EventSearchText, itinerary, _Places);

    if (itinerary == null) itinerary = "Main";
    let agenda = {};
    let allDates = [];
    for (let i = 0; i < this.events.length; i++) {
      const _itinerary = typeof this.events[i].Itinerary == "undefined" ? "Main" : this.events[i].Itinerary;
      if (_itinerary != itinerary) continue;
      if (EventSearchText != "") {
        if (!eventMatchesSearch(this.events[i], EventSearchText, _Places)) continue;
      }
      //console.log("getScheduleItems", this.events[i]);
      const date = new Date(this.events[i].Start);
      const strTime = date.toISOString().split("T")[0];
      //console.log("strTime", strTime);
      if (!allDates.includes(strTime)) {
        allDates.push(strTime);
        agenda[strTime] = [];
        //console.log(strTime);
      }
      const height = 60; // * (minutes / 15);
      agenda[strTime].push({
        event: this.events[i],
        height: height,
      });
    }
    //console.log("agenda", agenda);
    return agenda;
  }

  addEvent(event) {
    //console.log("Before", this.events.length);
    let new_event = new Appointment(this);
    new_event.copy(event);
    new_event.ID = this.create_id();
    this.events.push(new_event);
    this.events.sort(compare);
    return new_event;
    //console.log("After", this.events.length);
  }

  // Extracted verbatim from BuildMyTripWizard.js's identically-named local
  // closure (2026-09-16) so it's a plain, testable instance method instead
  // of logic trapped inside a React component — no behavior change. Places
  // one event for `place`, checking real opening hours (via
  // Place.getOpeningHoursConflict) and probing forward day-by-day (capped at
  // MAX_PROBE_DAYS) if today doesn't work, instead of blindly rolling to
  // tomorrow. Returns the event's End DateTime on success, or null if no day
  // within the trip's window (or within MAX_PROBE_DAYS) works for this place
  // at all — the caller then leaves it unscheduled rather than forcing a
  // known-bad placement.
  //
  // `options` (2026-10-30, for schedulePlacesInOrder's look-ahead): `dryRun`
  // returns the would-be End without adding the event, and `probe: false`
  // only tries the cursor's own day (no forward probing) — together they
  // answer "does this place fit today?" without side effects.
  addEventOpeningHoursAware(place, itinerary, dayDate, lastPlace, roundingTo, firstEventOfDay, startH, endH, placesModel, maxAllowedDate, paceMultiplier, hardEndTime, options = {}) {
    const travel_time = lastPlace !== null ? Common.estimateTravelTimeMinutes(lastPlace, place, roundingTo) : 0;
    const duration = place.Duration * (paceMultiplier || 1);

    // The guide's normal day-end hour (endH) is a preference here, not a
    // hard wall — a place that's genuinely open later just needs to fit
    // before this wider absolute ceiling instead. Doesn't change anything
    // for the common case (nothing else naturally pushes an event this late
    // unless accumulated travel time already did). The start floor (startH)
    // is hard for every later attempt, but the very first event of the
    // whole build (firstEventOfDay && lastPlace === null) is allowed to
    // start earlier — some callers (e.g. "Build Trip from Places") pin the
    // very first event to the user's exact picked Start datetime rather
    // than flooring it to startH first (Build My Trip's caller already
    // floors its own initial dayDate to startH before calling, so this
    // bypass is a no-op there — s.hour is never < startH to begin with).
    // Only applies to candidateStart/its same-day shift, not to later
    // probed days — those are fallback days, not the user's exact moment.
    const LATE_CEILING_H = 23;
    // hardEndTime (only set on a destination's actual departure day — see
    // buildScheduleFromAddedPlaces) overrides LATE_CEILING_H with a real,
    // precise cutoff instead of the generic late-evening ceiling — e.g. a
    // cruise ship's actual departure time, already padded with a boarding
    // buffer, so nothing gets scheduled to run right up to or past it.
    // hasSame(s, "day") matters here specifically: a place that opens very
    // late (e.g. 22:00) can shift `start` close to midnight, and a long
    // duration then pushes `end` past midnight into the next calendar day —
    // end.hour alone (e.g. 1am) reads as "< LATE_CEILING_H" numerically and
    // would wrongly pass without this check, spilling the event into the
    // following day instead of correctly being rejected for that slot.
    const fitsWindow = (s, e, allowEarlyStart = false) => {
      if ((s.hour < startH && !allowEarlyStart) || !s.hasSame(e, "day")) return false;
      if (hardEndTime && s.hasSame(hardEndTime, "day")) return e <= hardEndTime;
      return e.hour < LATE_CEILING_H || (e.hour === LATE_CEILING_H && e.minute === 0);
    };

    // Tries placing the visit starting at `candidateStart` — returns
    // {start, end} if it fits AND the place is actually open (shifting
    // later the same day if it's closed now but opens later within the
    // window), or null if this day just doesn't work for this place at all.
    const tryDay = (candidateStart, allowEarlyStart = false) => {
      const start = candidateStart;
      const end = start.plus({ minutes: duration });
      if (!fitsWindow(start, end, allowEarlyStart)) return null;
      const conflict = Place.getOpeningHoursConflict({ Places: place.ID, Start: start.toISO(), End: end.toISO() }, placesModel);
      if (conflict.status === "closed" && !conflict.closedAllDay && conflict.minutesUntilOpen != null) {
        const shiftedStart = start.plus({ minutes: conflict.minutesUntilOpen });
        const shiftedEnd = shiftedStart.plus({ minutes: duration });
        if (!fitsWindow(shiftedStart, shiftedEnd, allowEarlyStart)) return null;
        const shiftedConflict = Place.getOpeningHoursConflict({ Places: place.ID, Start: shiftedStart.toISO(), End: shiftedEnd.toISO() }, placesModel);
        if (shiftedConflict.status === "closed" || shiftedConflict.status === "closes_during") return null;
        return { start: shiftedStart, end: shiftedEnd };
      }
      if (conflict.status === "closed" || conflict.status === "closes_during") return null;
      return { start, end };
    };

    const isVeryFirstEvent = firstEventOfDay && lastPlace === null;
    let result = tryDay(firstEventOfDay ? dayDate : dayDate.plus({ minutes: travel_time }), isVeryFirstEvent);

    // Closed today (or the visit would run past closing) — rather than
    // blindly rolling to tomorrow and accepting whatever's there even if
    // it's ALSO closed, actually check each remaining day in the trip
    // window (capped at 3 weeks as a runaway backstop) and use the first
    // one this place is genuinely open on. If none of them work within the
    // trip's own day cap, skip the place entirely (return null) rather than
    // forcing a known-bad placement — the caller then leaves it
    // unscheduled, and "remove unused" can clean it up, instead of a
    // "closed all day" conflict landing in the built schedule. Matters most
    // for short trips, where there's no slack to reshuffle within a day.
    if (!result && options.probe !== false) {
      const MAX_PROBE_DAYS = 21;
      let probeDay = dayDate.startOf("day").plus({ days: 1 }).set({ hour: startH, minute: 0, second: 0, millisecond: 0 });
      for (let i = 0; i < MAX_PROBE_DAYS && (!maxAllowedDate || probeDay.startOf("day") < maxAllowedDate); i++) {
        result = tryDay(probeDay);
        if (result) break;
        probeDay = probeDay.plus({ days: 1 });
      }
    }

    if (!result) return null;
    // Optional day-count cap — the trip's own window may still reject a day
    // tryDay accepted on its own terms (tryDay only checks fitsWindow/
    // opening hours, not the trip's day cap).
    if (maxAllowedDate && result.start.startOf("day") >= maxAllowedDate) return null;

    // Round to the user's event_time_rounding increment so events land on
    // clean clock times (12:15) instead of the leftover fractional-minute
    // drift (12:14) from accumulating travel-time/duration minutes with no
    // rounding step. Rounds the returned cursor too, not just the display
    // values, so the next place's travel time accumulates from the rounded
    // point forward.
    //
    // Start is capped at 15 (Math.min(roundingTo, 15)), not the user's full
    // preference — roundDateTimeToNearestMinutes always rounds UP, so a
    // large rounding_to (e.g. 30) could inflate a short real travel gap
    // into the full increment (a previous End already aligned to 10:00 plus
    // a real 5-minute walk lands at 10:05, which ceiling-rounds to 10:30 —
    // a 30-minute gap for a 5-minute walk). End keeps the user's full
    // preference, matching recalculateSchedule/recalculateDay's identical
    // fix — only the travel-driven side is capped, not an intentional
    // event length. User-reported/found 2026-09-30.
    const roundedStart = Common.roundDateTimeToNearestMinutes(result.start, Math.min(roundingTo, 15));
    const roundedEnd = Common.roundDateTimeToNearestMinutes(result.end, roundingTo);
    if (options.dryRun) return roundedEnd;

    let event = new Appointment(this);
    event.Subject = place.Name;
    event.Body = "";
    event.Itinerary = itinerary;
    event.Places = place.ID;
    event.Start = roundedStart.toISO();
    event.End = roundedEnd.toISO();
    this.addEvent(event);

    return roundedEnd;
  }

  deleteEvent(event) {
    for (let i = 0; i < this.events.length; i++) {
      if (this.events[i].ID == event.ID) {
        this.events.splice(i, 1);
        return;
      }
    }
  }

  combineWithPreviousEvent(event, itinerary) {
    // event_index = -1;
    // for (let i = 0; i < this.events.length; i++) {
    //   if (this.events[i].ID == event.ID) {
    //     event_index = i;
    //     return;
    //   }
    // }
    console.log("combineWithPreviousEvent", event, itinerary);
    const event_index = this.events.findIndex((e) => e.ID === event.ID);
    if (event_index === -1) return;
    let prev_event_index = -1;
    let difference = 99999999999;
    const event_start_time = Common.LuxonDateTimeFromJSDate(new Date(this.events[event_index].Start));
    for (let i = 0; i < this.events.length; i++) {
      if (this.events[i].Itinerary !== itinerary) continue;
      if (this.events[i].ID === event.ID) continue;
      const cur_event_start_time = Common.LuxonDateTimeFromJSDate(new Date(this.events[i].Start));
      if (cur_event_start_time.day !== event_start_time.day) continue;
      if (cur_event_start_time.month !== event_start_time.month) continue;
      if (cur_event_start_time.year !== event_start_time.year) continue;
      if (cur_event_start_time.year > event_start_time.year) continue;
      //const new_diff = event_start_time.diff(cur_event_start_time);
      console.log("combineWithPreviousEvent", cur_event_start_time, event_start_time);
      //const interval = Interval.fromDateTimes(cur_event_start_time, event_start_time);
      const new_diff = event_start_time.toMillis() - cur_event_start_time.toMillis();
      console.log("combineWithPreviousEvent1", new_diff);
      if (new_diff > 0 && new_diff < difference) {
        console.log("combineWithPreviousEvent", new_diff);
        difference = new_diff;
        prev_event_index = i;
      }
    }
    if (prev_event_index === -1) return;
    console.log("combineWithPreviousEvent2", this.events[prev_event_index]);
    let placesString = this.events[event_index].Places;
    let placesArray = placesString.split(",");
    let prevplacesString = this.events[prev_event_index].Places;
    let prevplacesArray = prevplacesString.split(",");
    for (let j = 0; j < placesArray.length; j++) {
      if (!prevplacesArray.includes(placesArray[j])) {
        this.events[prev_event_index].Places += "," + placesArray[j];
      }
    }
    this.events[prev_event_index].Body += " " + this.events[event_index].Body;
    this.events[prev_event_index].End = this.events[event_index].End;
    this.events.splice(event_index, 1);
    console.log("combineWithPreviousEvent3", this.events[prev_event_index]);
  }

  combineWithEvents(event, events) {
    console.log("combineWithEvents1");
    let prev_event_index = this.events.findIndex((e) => e.ID === event.ID);
    if (prev_event_index === -1) return;
    const event_end_time = Common.LuxonDateTimeFromJSDate(new Date(this.events[prev_event_index].End));
    let event_end_time_index = prev_event_index;
    let prevplacesString = this.events[prev_event_index].Places;
    let prevplacesArray = prevplacesString.split(",");
    for (let i = 0; i < events.length; i++) {
      if (event.ID === events[i].id) continue;
      const event_index = this.events.findIndex((e) => e.ID === events[i].id);
      if (event_index === -1) continue;
      const _event_end_time = Common.LuxonDateTimeFromJSDate(new Date(this.events[event_index].End));
      if (_event_end_time > event_end_time) event_end_time_index = event_index;
      let placesString = this.events[event_index].Places;
      let placesArray = placesString.split(",");
      for (let j = 0; j < placesArray.length; j++) {
        if (!prevplacesArray.includes(placesArray[j])) {
          this.events[prev_event_index].Places += "," + placesArray[j];
        }
      }
      this.events[prev_event_index].Body += " " + this.events[event_index].Body;
      this.events[prev_event_index].End = this.events[event_end_time_index].End;
    }
    for (let i = 0; i < events.length; i++) {
      if (event.ID === events[i].id) continue;
      const delete_index = this.events.findIndex((e) => e.ID === events[i].id);
      if (delete_index === -1) continue;
      this.events.splice(delete_index, 1);
    }
  }

  modifyEvent(event) {
    for (let i = 0; i < this.events.length; i++) {
      if (this.events[i].ID == event.ID) {
        //this.events[i].copy(event);
        this.events[i].Subject = event.Subject;
        this.events[i].Body = event.Body;
        //remove timezone
        this.events[i].Start = event.Start; //Common.dateToString(event.Start); //event.Start; //Common.dateToString(event.Start); //
        this.events[i].End = event.End; //Common.dateToString(event.End); //event.End; //
        //console.log("modifyEvent", this.events[i]);
        this.events[i].Places = event.Places;
        this.events[i].Source = Place_Source.USER;
        if (typeof event.Itinerary != "undefined") this.events[i].Itinerary = event.Itinerary;
        break;
      }
    }
  }

  updateSchedule() {
    for (let i = 0; i < this.events.length; i++) {
      //console.log("this.events[i]", this.events[i]);
      this.events[i].Start = Common.LuxonDateTimeToString(
        Common.LuxonDateTimeFromJSDate(new Date(this.events[i].Start))
      ); //event.Start; //Common.dateToString(event.Start); //
      //console.log("this.events[i].Start", this.events[i].Start);
      this.events[i].End = Common.LuxonDateTimeToString(Common.LuxonDateTimeFromJSDate(new Date(this.events[i].End))); //event.End; //
      this.events[i].Places = this.events[i].Places;
      this.events[i].Source = Place_Source.USER;
      this.events[i].Itinerary = "Main";
    }
  }

  deleteAllEvents(itinerary) {
    if (itinerary == null) itinerary = "Main";
    let new_events = [];
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary !== itinerary) new_events.push(event);
    });
    this.events = new_events;
  }

  renameSchedule(oldItinerary, newItinerary, guide) {
    //console.log("renameSchedule", oldItinerary, newItinerary);
    this.events.forEach((event) => {
      if (event.Itinerary == oldItinerary) event.Itinerary = newItinerary;
    });

    //console.log("renameSchedule: ", this.events);

    guide.document = guide.document
      .split(`<a href="schedulelink:${oldItinerary}">`)
      .join(`<a href="schedulelink:${newItinerary}">`);
    guide.document = guide.document
      .split(`<a href="itinerarylink:${oldItinerary}">`)
      .join(`<a href="itinerarylink:${newItinerary}">`);

    for (let i = 0; i < guide.chapters.length; i++) {
      for (let j = 0; j < guide.chapters[i].sections.length; j++) {
        guide.chapters[i].sections[j].html = guide.chapters[i].sections[j].html
          .split(`<a href="schedulelink:${oldItinerary}">`)
          .join(`<a href="schedulelink:${newItinerary}">`);
        guide.chapters[i].sections[j].html = guide.chapters[i].sections[j].html
          .split(`<a href="itinerarylink:${oldItinerary}">`)
          .join(`<a href="itinerarylink:${newItinerary}">`);
      }
    }

    //console.log("renameSchedule: ", guide.document);
    //TODO rename schedule names in html
  }

  copySchedule(itinerary_from, itinerary_to) {
    //console.log("copySchedule", itinerary_from, itinerary_to);
    if (itinerary_to == null) itinerary_to = "Main";
    if (itinerary_from == itinerary_to) return;
    let new_events = [];
    this.events.forEach((event) => {
      const _itinerary = typeof event.Itinerary == "undefined" ? "Main" : event.Itinerary;
      if (_itinerary == itinerary_from) {
        let new_event = new Appointment(this);
        new_event.copy(event);
        new_event.Itinerary = itinerary_to;
        new_events.push(new_event);
      }
    });
    //console.log("new_events", new_events);
    //this.deleteAllEvents(itinerary_to);
    this.events = this.events.concat(new_events);
  }

  // delete_event(eventID) {
  //   var eventIndex = this.get_event_index(eventID);
  //   if (eventIndex == -1) return;
  //   this.events.splice(eventIndex, 1);
  //   this.getPlaces_InSchedule();
  //   storeData("LG_events", this.events);
  // }
  change_start_date(newdate) {
    if (this.events.length == 0) return;
    var schedDate = new Date(this.events[0].Start);
    var diff = newdate - schedDate;
    for (let i = 0; i < this.events.length; i++) {
      var event = this.events[i];
      var sdate = new Date(event.Start);
      var edate = new Date(event.End);
      sdate.setMilliseconds(sdate.getMilliseconds() + diff);
      edate.setMilliseconds(edate.getMilliseconds() + diff);
      event.Start = Common.dateToString(sdate);
      event.End = Common.dateToString(edate);
    }
  }
  // get_first_event(itinerary) {
  //   if (itinerary == null) itinerary = "Main";
  //   if (this.events.length == 0) return null;
  //   return this.events[0];
  // }
  // Companion to Place.getOpeningHoursConflict — for a conflicted event
  // (its place doesn't fit its scheduled time), finds other events in the
  // same itinerary — any day, not just this one, since a place that's
  // closed all day has no same-day fix at all, only a different day can
  // work — whose place could trade time slots with it. A one-way check
  // ("would this place fit in that slot") isn't enough, since it could just
  // move the conflict onto the other event instead of resolving it — both
  // directions must hold: the conflicted event's place must fit the
  // candidate's original slot, AND the candidate's place must fit the
  // conflicted event's original slot.
  // Ranked best-first: closest to the conflicted event's place wins (less
  // disruption to the rest of that day's geography for both places
  // involved), ties broken by the more similar duration (swapping a
  // 30-minute stop's slot with a 3-hour one, while technically valid,
  // leaves one of them with a badly mismatched amount of time).
  // The area an event's day already covers (2026-11-03), to flag a swap or
  // replace candidate that's far from it ("Far from this day's other stops"):
  // a walking day in a city offered a stop 3.5 hours away, still a valid
  // choice but easy to pick by mistake. The day's other stops set the reach
  // (1.5× the farthest of them from this place), at least 3 km, at most 50;
  // a day with no other placed stop gets 15 km.
  // An event's Start/End as an ISO string: stored ones are strings, but an
  // event edited in the app can hold a Date or a luxon DateTime (2026-11-03:
  // "(e.Start || "").slice is not a function" in the fix dialog).
  static isoOf(v) {
    if (typeof v === "string") return v;
    if (v && typeof v.toISO === "function") return v.toUTC().toISO();
    if (v instanceof Date && !isNaN(v)) return v.toISOString();
    return "";
  }

  static dayReachKm(event, events, placesModel) {
    const placeOf = (e) => {
      const id = (e.Places || "").split(",").map((x) => x.trim()).filter(Boolean)[0];
      return id ? placesModel.getPlace(id) : null;
    };
    const here = placeOf(event);
    if (!here) return 50;
    const day = Schedule.isoOf(event.Start).slice(0, 10);
    const itin = event.Itinerary || "Main";
    let far = -1;
    (events || []).forEach((e) => {
      if (e === event || e.ID === event.ID || Schedule.isoOf(e.Start).slice(0, 10) !== day || (e.Itinerary || "Main") !== itin) return;
      const p = placeOf(e);
      if (!p || p.Lat == null || p.Longi == null) return;
      far = Math.max(far, Common.calculateDistance({ latitude: here.Lat, longitude: here.Longi }, { latitude: p.Lat, longitude: p.Longi }));
    });
    return far < 0 ? 15 : Math.min(50, Math.max(3, far * 1.5));
  }

  // After a swap or replace puts a new place in a day (2026-11-03), the stops
  // after it keep their times even when the new place is farther away, so the
  // gap no longer covers the trip. Pushes each later stop of that day just far
  // enough (keeping its length) for the travel from the stop before; never pulls
  // a stop earlier. Returns how many stops moved.
  static makeRoomForTravel(events, placesModel, dayISODate, itinerary = "Main") {
    const placeOf = (e) => {
      const id = (e.Places || "").split(",").map((x) => x.trim()).filter(Boolean)[0];
      return id ? placesModel.getPlace(id) : null;
    };
    const iso = Schedule.isoOf;
    const day = (events || [])
      .filter((e) => iso(e.Start).slice(0, 10) === dayISODate && (e.Itinerary || "Main") === itinerary)
      .sort((a, b) => (iso(a.Start) < iso(b.Start) ? -1 : iso(a.Start) > iso(b.Start) ? 1 : 0));
    let moved = 0;
    for (let k = 1; k < day.length; k++) {
      const prev = day[k - 1], cur = day[k];
      const a = placeOf(prev), b = placeOf(cur);
      if (!a || !b || prev.IsTravel || cur.IsTravel) continue;
      const need = DateTime.fromISO(iso(prev.End), { zone: "utc" }).plus({ minutes: Common.estimateTravelTimeMinutes(a, b, 5) });
      const start = DateTime.fromISO(iso(cur.Start), { zone: "utc" });
      if (!need.isValid || !start.isValid || start >= need) continue;
      const len = DateTime.fromISO(iso(cur.End), { zone: "utc" }).diff(start, "minutes").minutes;
      cur.Start = need.toISO();
      cur.End = need.plus({ minutes: len }).toISO();
      moved++;
    }
    return moved;
  }

  static findSwapCandidates(conflictedEvent, itineraryEvents, placesModel) {
    const wouldFit = (placesStr, start, end) => {
      const status = Place.getOpeningHoursConflict({ Places: placesStr, Start: start, End: end }, placesModel).status;
      return status !== "closed" && status !== "closes_during";
    };
    const firstPlace = (placesStr) => {
      const id = (placesStr || "").split(",").map((x) => x.trim()).filter(Boolean)[0];
      return id ? placesModel.getPlace(id) : null;
    };
    const durationMinutes = (start, end) => {
      const d = DateTime.fromISO(end, { zone: "utc" }).diff(DateTime.fromISO(start, { zone: "utc" }), "minutes").minutes;
      return Number.isFinite(d) ? d : 0;
    };

    const conflictedPlace = firstPlace(conflictedEvent.Places);
    const conflictedDuration = durationMinutes(conflictedEvent.Start, conflictedEvent.End);

    // Swap partners must be near each other — "any day" is fine within one
    // destination, but in a multi-city trip it traded a closed-on-Monday
    // Milan museum with a Florence stop, putting each in the wrong city's
    // day (the train between them made it physically impossible). Places
    // with no coordinates count as near (calculateDistance returns 0).
    // Found 2026-09-30.
    const MAX_SWAP_DISTANCE_KM = 50;
    const isNearby = (other) => {
      const otherPlace = firstPlace(other.Places);
      if (!conflictedPlace || !otherPlace) return true;
      return (
        Common.calculateDistance(
          { latitude: conflictedPlace.Lat, longitude: conflictedPlace.Longi },
          { latitude: otherPlace.Lat, longitude: otherPlace.Longi }
        ) <= MAX_SWAP_DISTANCE_KM
      );
    };

    const candidates = (itineraryEvents || []).filter((other) => {
      if (other === conflictedEvent || other.ID === conflictedEvent.ID) return false;
      if (!other.Places) return false;
      if (!isNearby(other)) return false;
      return (
        wouldFit(conflictedEvent.Places, other.Start, other.End) &&
        wouldFit(other.Places, conflictedEvent.Start, conflictedEvent.End)
      );
    });

    return candidates
      .map((other) => {
        const otherPlace = firstPlace(other.Places);
        const distance =
          conflictedPlace && otherPlace
            ? Common.calculateDistance(
                { latitude: conflictedPlace.Lat, longitude: conflictedPlace.Longi },
                { latitude: otherPlace.Lat, longitude: otherPlace.Longi }
              )
            : Infinity;
        const durationDiff = Math.abs(conflictedDuration - durationMinutes(other.Start, other.End));
        return { event: other, distance, durationDiff };
      })
      .sort((a, b) => (a.distance !== b.distance ? a.distance - b.distance : a.durationDiff - b.durationDiff))
      .map((x) => x.event);
  }

  // One-directional counterpart to findSwapCandidates: instead of trading
  // times with another *scheduled* event, substitutes the conflicted
  // event's place for one that's in the guide but not used in any
  // schedule yet — same "unused" definition PlacesHeader.js's
  // RemoveUnusedPlaces uses (a place ID that appears in no event's Places
  // field, across every schedule, not just this event's own itinerary).
  // Only needs a one-way fit check (would this unused place's hours allow
  // it at the conflicted event's existing time) since nothing else is
  // being moved — the unused place isn't currently anywhere, so there's no
  // second slot to also validate. Raised 2026-09-03 as
  // project_deferred_unused_places_pruning_idea, built 2026-09-17 once the
  // premise (unused places got auto-deleted before this could ever run)
  // turned out to no longer hold.
  // The events whose places count as "already used" when suggesting places
  // for `itinerary` (Swap/Replace, Fill idle time, the event place list).
  // A guide's schedules are separate plans by default — a Featured guide's
  // 1-day … 7-day samples share one place list, so a place in the 7-day
  // schedule is still a candidate for the 1-day one. A guide whose schedules
  // are parts of ONE trip (e.g. "Week 1" / "Week 2") turns on
  // guide_settings.schedules_share_places in Guide Settings. 2026-10-16.
  static eventsCountedAsUsed(itinerary, guide = typeof window !== "undefined" ? window.guide_obj : null) {
    const events = guide?.schedule?.events || [];
    if (guide?.guide_settings?.schedules_share_places === true) return events;
    const name = (itinerary || "Main").replace(/%20/g, " ");
    return events.filter((e) => (typeof e.Itinerary === "undefined" ? "Main" : e.Itinerary.replace(/%20/g, " ")) === name);
  }

  static findReplaceCandidates(conflictedEvent, allScheduleEvents, placesModel) {
    const wouldFit = (placesStr, start, end) => {
      const status = Place.getOpeningHoursConflict({ Places: placesStr, Start: start, End: end }, placesModel).status;
      return status !== "closed" && status !== "closes_during";
    };
    const firstPlace = (placesStr) => {
      const id = (placesStr || "").split(",").map((x) => x.trim()).filter(Boolean)[0];
      return id ? placesModel.getPlace(id) : null;
    };

    const referencedIds = new Set();
    (allScheduleEvents || []).forEach((e) => {
      (e.Places || "").split(",").map((id) => id.trim()).filter(Boolean).forEach((id) => referencedIds.add(id));
    });

    const conflictedPlace = firstPlace(conflictedEvent.Places);
    const unusedPlaces = (placesModel.placesList || []).filter((p) => !referencedIds.has(String(p.ID)));

    const candidates = unusedPlaces.filter((p) => wouldFit(String(p.ID), conflictedEvent.Start, conflictedEvent.End));

    // Same cross-city guard as findSwapCandidates — an unused place that's
    // open at this time but in a different city entirely isn't a real
    // substitute (the distance was only used for sort order below, never
    // to actually exclude anything). A place with no coordinates counts as
    // near, matching findSwapCandidates. Found 2026-09-30.
    const MAX_REPLACE_DISTANCE_KM = 50;

    return candidates
      .map((place) => ({
        place,
        distance:
          conflictedPlace
            ? Common.calculateDistance(
                { latitude: conflictedPlace.Lat, longitude: conflictedPlace.Longi },
                { latitude: place.Lat, longitude: place.Longi }
              )
            : Infinity,
      }))
      .filter(({ distance }) => !conflictedPlace || distance <= MAX_REPLACE_DISTANCE_KM)
      .sort((a, b) => a.distance - b.distance)
      .map((x) => x.place);
  }

  // Candidate-finder for filling idle time at the end of a day (see
  // SchedulePanel.js's reportIdleTime/openFillIdleTime) — unlike
  // findReplaceCandidates (substitutes ONE event's place at its existing
  // fixed time), this proposes ADDING a brand new event into open time
  // after the day's last event. Distance is measured from the day's actual
  // last place (anchorPlace), same 50km guard as findSwapCandidates/
  // findReplaceCandidates. Only a rough pre-filter (own Duration fits the
  // window, opening hours allow a visit starting right at windowStart) —
  // the caller places the actual event via addEventOpeningHoursAware, which
  // also accounts for real travel time and may still reject a candidate
  // this passed. Found 2026-09-30.
  static findFillCandidates(anchorPlace, windowStart, windowEnd, allScheduleEvents, placesModel) {
    const referencedIds = new Set();
    (allScheduleEvents || []).forEach((e) => {
      (e.Places || "").split(",").map((id) => id.trim()).filter(Boolean).forEach((id) => referencedIds.add(id));
    });
    const unusedPlaces = (placesModel.placesList || []).filter((p) => !referencedIds.has(String(p.ID)));

    const MAX_FILL_DISTANCE_KM = 50;
    const windowMinutes = windowEnd.diff(windowStart, "minutes").minutes;

    return unusedPlaces
      .map((place) => ({
        place,
        distance: anchorPlace
          ? Common.calculateDistance(
              { latitude: anchorPlace.Lat, longitude: anchorPlace.Longi },
              { latitude: place.Lat, longitude: place.Longi }
            )
          : 0,
      }))
      .filter(({ distance }) => !anchorPlace || distance <= MAX_FILL_DISTANCE_KM)
      .filter(({ place }) => (place.Duration || 90) <= windowMinutes)
      .filter(({ place }) => {
        const start = windowStart;
        const end = start.plus({ minutes: place.Duration || 90 });
        const status = Place.getOpeningHoursConflict({ Places: String(place.ID), Start: start.toISO(), End: end.toISO() }, placesModel).status;
        return status !== "closed" && status !== "closes_during";
      })
      .sort((a, b) => a.distance - b.distance)
      .map((x) => x.place);
  }

  // Auto-fix pass, swap case: a conflicted event (closed all day, or closes
  // partway through the visit) with a valid swap available. findSwapCandidates
  // already guarantees both directions fit (each place would also be fine at
  // the other's original time), and "Swap or Replace Event" already
  // lets the user manually redo any swap afterward regardless of conflict
  // state — so applying the best (closest/most duration-matched) candidate
  // here automatically is low-risk. Originally closed-all-day only; widened
  // 2026-09-14 to also cover closes_during (same swap mechanics — a place
  // that closes mid-visit is just as fixable by trading slots with another
  // event — findSwapCandidates already treats both statuses identically via
  // its own wouldFit check). Run autoResolvePushPullConflicts first — it
  // catches the same closes_during case with a smaller, less disruptive fix
  // (shortening/shifting this one event alone) whenever the shift is small
  // enough; this pass is the fallback for whatever that couldn't fix. Run
  // once, right after a schedule's events are all in place. Found/requested
  // 2026-09-03; widened 2026-09-14.
  static autoResolveClosedAllDayConflicts(events, placesModel) {
    let fixedCount = 0;
    (events || []).forEach((event) => {
      const conflict = Place.getOpeningHoursConflict(event, placesModel);
      const isFixableClosed = conflict.status === "closed" && conflict.closedAllDay;
      const isFixableClosesDuring = conflict.status === "closes_during";
      if (!isFixableClosed && !isFixableClosesDuring) return;
      const candidate = Schedule.findSwapCandidates(event, events, placesModel)[0];
      if (!candidate) return;
      const tmpStart = event.Start;
      const tmpEnd = event.End;
      event.Start = candidate.Start;
      event.End = candidate.End;
      candidate.Start = tmpStart;
      candidate.End = tmpEnd;
      fixedCount++;
    });
    return fixedCount;
  }

  // Auto-fix pass, push-start/pull-end case: a same-day fix for a small
  // conflict, tried BEFORE the swap pass above since it's less disruptive
  // (only this one event's own time moves/shrinks, nothing else changes).
  // Only applies when the needed adjustment is under 25% of the event's own
  // duration — a bigger shift is more likely to meaningfully change the
  // visit than "fix" it, and is left to the swap pass or manual editing.
  // - "closed" (opens later the same day, not closedAllDay): pushes both
  //   Start and End later by the wait, preserving the full visit duration —
  //   but only if that doesn't newly push the event past the start of the
  //   next event that same day.
  // - "closes_during": pulls End back to the place's actual closing time,
  //   shortening the visit — always safe re: neighbors, since shortening an
  //   event can only widen the gap to whatever follows it, never overlap it.
  // Requested 2026-09-03, implemented 2026-09-14 alongside the
  // closes_during widening above.
  static autoResolvePushPullConflicts(events, placesModel) {
    let fixedCount = 0;
    const byDay = new Map();
    (events || []).forEach((event) => {
      const day = DateTime.fromISO(event.Start, { zone: "utc" }).toISODate();
      if (!day) return;
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push(event);
    });
    byDay.forEach((dayEvents) => {
      dayEvents.sort((a, b) => DateTime.fromISO(a.Start, { zone: "utc" }) - DateTime.fromISO(b.Start, { zone: "utc" }));
      dayEvents.forEach((event, idx) => {
        const conflict = Place.getOpeningHoursConflict(event, placesModel);
        const start = DateTime.fromISO(event.Start, { zone: "utc" });
        const end = DateTime.fromISO(event.End, { zone: "utc" });
        const durationMinutes = end.diff(start, "minutes").minutes;
        if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return;
        const maxShift = durationMinutes * 0.25;

        if (conflict.status === "closed" && !conflict.closedAllDay && conflict.minutesUntilOpen != null) {
          const shift = conflict.minutesUntilOpen;
          if (shift <= 0 || shift > maxShift) return;
          const newEnd = end.plus({ minutes: shift });
          const next = dayEvents[idx + 1];
          if (next && newEnd > DateTime.fromISO(next.Start, { zone: "utc" })) return;
          event.Start = start.plus({ minutes: shift }).toISO();
          event.End = newEnd.toISO();
          fixedCount++;
        } else if (conflict.status === "closes_during" && conflict.minutesUntilClose != null) {
          const shrink = durationMinutes - conflict.minutesUntilClose;
          if (shrink <= 0 || shrink > maxShift) return;
          event.End = start.plus({ minutes: conflict.minutesUntilClose }).toISO();
          fixedCount++;
        }
      });
    });
    return fixedCount;
  }

  get_event(eventID) {
    for (var key in this.events) {
      var event = this.events[key];
      if (event.ID == eventID) return event;
    }
    return null;
  }
  // get_event_index(eventID) {
  //   for (let i = 0; i < this.events.length; i++) {
  //     var event = this.events[i];
  //     if (event.ID == eventID) return i;
  //   }
  //   return -1;
  // }
}
function compare(a, b) {
  var adate = new Date(a.Start);
  var bdate = new Date(b.Start);
  if (adate < bdate) return -1;
  if (adate > bdate) return 1;
  return 0;
}
